'use client';

import { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Stat {
  id: string;
  value: number;
  suffix: string;
  label: string;
  icon: string;
}

export default function ImpactStatsSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const stats: Stat[] = [
    { id: 'stat_impressions_full', value: 20, suffix: 'M+', label: 'Total Impressions', icon: 'ChartBarIcon' },
    { id: 'stat_partnerships_full', value: 20, suffix: '+', label: 'Brand Partnerships', icon: 'HandRaisedIcon' },
    { id: 'stat_presentations', value: 12, suffix: '', label: 'Research Presentations', icon: 'PresentationChartLineIcon' },
    { id: 'stat_internships', value: 12, suffix: '+', label: 'Industry Internships', icon: 'BriefcaseIcon' },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-32 px-6 bg-secondary">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-20">
          <p className="text-micro text-muted-foreground mb-4">Impact By Numbers</p>
          <h2 className="text-5xl font-bold tracking-tight text-foreground mb-6">
            Creating Measurable Change
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            From research labs to global platforms, every number represents lives touched and barriers broken.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <StatCard key={stat.id} stat={stat} isVisible={isVisible} delay={index * 100} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StatCard({ stat, isVisible, delay }: { stat: Stat; isVisible: boolean; delay: number }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000;
    const steps = 60;
    const increment = stat.value / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= stat.value) {
        setCount(stat.value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [isVisible, stat.value]);

  return (
    <div
      className={`bg-white p-8 rounded-2xl border border-border shadow-sm text-center transition-all duration-700 hover:shadow-md hover:scale-105 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="inline-flex p-4 bg-foreground/5 rounded-xl mb-6">
        <Icon name={stat.icon as any} size={32} className="text-foreground" />
      </div>
      <div className="text-5xl font-bold text-foreground mb-2">
        {count}
        {stat.suffix}
      </div>
      <p className="text-sm font-medium text-muted-foreground uppercase tracking-wider">{stat.label}</p>
    </div>
  );
}